// src/components/Admin/ItemManager/ItemTable.jsx
import {
  Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Chip, Box,
  Pagination, TextField, Select, MenuItem, Stack, Typography,
  useTheme, useMediaQuery
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useState, useMemo } from 'react';

const getFullImageUrl = (url) =>
  url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

const rowsPerPage = 5;

export default function ItemTable({ items = [], onEdit, onDelete, onToggleActive }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const filteredItems = useMemo(() => (
    items
      .filter((it) => {
        if (filter === 'active') return it.is_active;
        if (filter === 'inactive') return !it.is_active;
        return true;
      })
      .filter((it) =>
        !search.trim() || it.name?.toLowerCase().includes(search.toLowerCase()))
  ), [items, search, filter]);

  const totalPages = Math.ceil(filteredItems.length / rowsPerPage) || 1;
  const paginatedItems = filteredItems.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const handleChangePage = (_, v) => setPage(v);
  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
  const handleFilterChange = (e) => { setFilter(e.target.value); setPage(1); };

  return (
    <Box
      sx={{
        '@media (max-width:600px)': {
          transform: 'scale(0.9)',
          transformOrigin: 'top left',
        },
      }}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        alignItems="center"
      >
        <TextField
          label="아이템명 검색"
          size="small"
          value={search}
          onChange={handleSearchChange}
          fullWidth={isMobile}
        />
        <Select
          size="small"
          value={filter}
          onChange={handleFilterChange}
          fullWidth={isMobile}
        >
          <MenuItem value="all">전체</MenuItem>
          <MenuItem value="active">노출 중</MenuItem>
          <MenuItem value="inactive">숨김</MenuItem>
        </Select>
        <Typography
          variant="body2"
          sx={{
            ml: isMobile ? 0 : 'auto',
            mt: isMobile ? 1 : 0,
            textAlign: isMobile ? 'right' : 'left',
          }}
        >
          총 {filteredItems.length}건
        </Typography>
      </Stack>

      <Box
        sx={{
          '@media (max-width:600px)': {
            width: 'max-content',
            overflowX: 'auto',
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>미리보기</TableCell>
              <TableCell>이름</TableCell>
              <TableCell>가격</TableCell>
              <TableCell>재고</TableCell>
              <TableCell>타입</TableCell>
              <TableCell>노출 상태</TableCell>
              <TableCell>작업</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  🔍 조건에 맞는 아이템이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((it) => (
                <TableRow key={it.id}>
                  <TableCell align="center">
                    {it.image ? (
                      <img
                        src={getFullImageUrl(it.image)}
                        alt={it.name}
                        width={36}
                        height={36}
                        style={{ borderRadius: 4, objectFit: 'cover' }}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell>{it.name}</TableCell>
                  <TableCell>{it.price} Leaf🌿</TableCell>
                  <TableCell>
                    {it.is_unlimited
                      ? '무제한'
                      : `${it.remaining ?? 0} / ${it.stock ?? 0}`}
                  </TableCell>
                  <TableCell>{it.is_coupon ? '쿠폰' : '일반'}</TableCell>
                  <TableCell>
                    <Chip
                      label={it.is_active ? '노출 중' : '숨김'}
                      color={it.is_active ? 'success' : 'default'}
                      size="small"
                      icon={it.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      onClick={() => onToggleActive(it.id, it.is_active)}
                      clickable
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => onEdit(it)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => onDelete(it.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
            sx={{
              '@media (max-width:600px)': { transform: 'scale(0.9)' },
            }}
          />
        </Box>
      )}
    </Box>
  );
}